import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

// Query parameters whose values must never reach telemetry (e.g. the one-time
// password-reset token carried by /reset-password?token=..., and the seat invite
// redeem code carried by /redeem?code=...).
const SENSITIVE_QUERY_PARAMS = ["token", "code"];

const REDACTED_VALUE = "REDACTED";

/**
 * Masks sensitive query parameter values in a URL while preserving the path
 * and all other query parameters. Non-URL strings are returned unchanged.
 */
export function sanitizeTelemetryUrl(url: string): string {
  if (!url.includes("?")) return url;

  try {
    const parsed = new URL(url, window.location.origin);
    let changed = false;
    for (const param of SENSITIVE_QUERY_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, REDACTED_VALUE);
        changed = true;
      }
    }
    return changed ? parsed.toString() : url;
  } catch {
    return url;
  }
}

// Telemetry item fields that can carry a URL (page views, dependencies, ajax).
const URL_BEARING_FIELDS = ["uri", "refUri", "url", "target", "data", "name"] as const;

function sanitizeTelemetryItem(item: { baseData?: Record<string, unknown> }) {
  if (!item.baseData) return;

  for (const field of URL_BEARING_FIELDS) {
    const value = item.baseData[field];
    if (typeof value === "string") {
      item.baseData[field] = sanitizeTelemetryUrl(value);
    }
  }
}

class TelemetryService {
  private appInsights: ApplicationInsights | null = null;
  private reactPlugin: ReactPlugin | null = null;
  private initialized = false;

  initialize(connectionString?: string) {
    if (this.initialized || !connectionString) {
      console.log("Telemetry: Skipping initialization", {
        initialized: this.initialized,
        hasConnectionString: !!connectionString,
      });
      return;
    }

    try {
      this.reactPlugin = new ReactPlugin();

      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          // No ReactPlugin `history`: it needs a history-library object (listen/location), not
          // window.history. Route-change page views come from enableAutoRouteTracking below.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          extensions: [this.reactPlugin as unknown as any],
          enableAutoRouteTracking: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          enableCorsCorrelation: true,
          disableFetchTracking: false,
          enableUnhandledPromiseRejectionTracking: true,
          autoTrackPageVisitTime: true,
          samplingPercentage: import.meta.env.PROD ? 10 : 100, // 10% sampling in production
          disableExceptionTracking: false,
          disableAjaxTracking: false,
          maxBatchSizeInBytes: 10000,
          maxBatchInterval: 15000,
          enableDebug: import.meta.env.DEV,
          loggingLevelConsole: import.meta.env.DEV ? 2 : 0,
          loggingLevelTelemetry: 1,
        },
      });

      this.appInsights.loadAppInsights();

      // Strip sensitive query values (reset tokens) from every telemetry item,
      // including automatic route-change page views.
      this.appInsights.addTelemetryInitializer(sanitizeTelemetryItem);

      // Set authenticated user context if available
      const user = this.getCurrentUser();
      if (user?.id) {
        this.appInsights.setAuthenticatedUserContext(user.id, user.accountId);
      }

      // Track initial page view with an explicitly sanitized URI — the SDK
      // defaults a missing uri to the raw location.href.
      this.appInsights.trackPageView({ uri: sanitizeTelemetryUrl(window.location.href) });

      this.initialized = true;
      console.log("Telemetry: Application Insights initialized successfully");
    } catch (error) {
      console.error("Telemetry: Failed to initialize Application Insights", error);
    }
  }

  private getCurrentUser() {
    // Get user from your auth context/store
    // This is a placeholder - integrate with your actual auth system
    try {
      const authData = localStorage.getItem("auth");
      if (authData) {
        const parsed = JSON.parse(authData);
        return {
          id: parsed.userId,
          accountId: parsed.accountId,
        };
      }
    } catch {
      // Ignore errors getting user
    }
    return null;
  }

  // Track custom events
  trackEvent(
    name: string,
    properties?: Record<string, unknown>,
    measurements?: Record<string, number>,
  ) {
    if (!this.appInsights) return;

    this.appInsights.trackEvent({
      name,
      properties,
      measurements,
    });
  }

  // Track exceptions
  trackException(error: Error, properties?: Record<string, unknown>) {
    if (!this.appInsights) return;

    this.appInsights.trackException({
      exception: error,
      properties,
      severityLevel: 2, // Error
    });
  }

  // Track page views
  trackPageView(name?: string, url?: string, properties?: Record<string, unknown>) {
    if (!this.appInsights) return;

    this.appInsights.trackPageView({
      name,
      uri: sanitizeTelemetryUrl(url ?? window.location.href),
      properties,
    });
  }

  // Track metrics
  trackMetric(name: string, average: number, properties?: Record<string, unknown>) {
    if (!this.appInsights) return;

    this.appInsights.trackMetric({
      name,
      average,
      properties,
    });
  }

  // Track API calls
  trackDependency(
    name: string,
    commandName: string,
    duration: number,
    success: boolean,
    properties?: Record<string, unknown>,
  ) {
    if (!this.appInsights) return;

    this.appInsights.trackDependencyData({
      id: `${Date.now()}`,
      name,
      duration,
      success,
      responseCode: success ? 200 : 500,
      type: "HTTP",
      data: commandName,
      properties,
    });
  }

  // Track user actions
  trackUserAction(action: string, properties?: Record<string, unknown>) {
    this.trackEvent(`UserAction.${action}`, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
  }

  // Track business metrics
  trackBusinessEvent(category: string, action: string, properties?: Record<string, unknown>) {
    this.trackEvent(`Business.${category}.${action}`, {
      ...properties,
      category,
      action,
      timestamp: new Date().toISOString(),
    });
  }

  // Get React plugin for React components
  getReactPlugin() {
    return this.reactPlugin;
  }

  // Flush any pending telemetry
  flush() {
    if (!this.appInsights) return;
    this.appInsights.flush();
  }
}

// Export singleton instance
export const telemetry = new TelemetryService();

// Export types for use in components
export type { TelemetryService };
